/**
 * Pure-function decision policy for the Service Worker notification render
 * AFTER the basic SWRenderPolicy + thumbnail/tombstone/edit polish step.
 *
 * Where `SWRenderPolicy` decides "generic vs rendered" from the freshly
 * decrypted envelope, this policy decides the FINAL render shape by
 * applying the three SW-polish invariants:
 *
 *   1. Tombstone collapses the body to `[deleted]`. If a tombstone has been
 *      recorded for `(messageId, originalAuthorId)` — even if it arrived out
 *      of order BEFORE the cleartext — the body never reaches this function:
 *      the caller substitutes `[deleted]` via `composeNotificationBody` and
 *      this policy receives that already-collapsed body. The threat invariant
 *      is enforced by funnelling all body composition through one place.
 *
 *   2. Edits append `(edited)` exactly once. Same composer.
 *
 *   3. Thumbnails come from the local cache or not at all. The caller passes
 *      a `NotificationThumbnailResolution`; if it is `{ kind: 'missing' }`,
 *      the rendered notification carries no `iconBytes`. There is no third
 *      shape (remote fetch path) by closed-union construction.
 *
 * Pure-logic only: no I/O, no DOM access. The SW push handler performs the
 * side effects (cache reads, store lookups) BEFORE calling this policy and
 * passes the results in.
 *
 * Mirrors the iOS NSE polish counterpart `NSENotificationRenderPolicy.swift`.
 */

import { composeNotificationBody } from "./SWBodyComposer";
import type { NotificationThumbnailResolution } from "./SWThumbnailResolver";

// ---------------------------------------------------------------------------
// Input shape
// ---------------------------------------------------------------------------

/**
 * Inputs the SW push handler assembles BEFORE calling the policy.
 *
 * - `body` is the latest decrypted body (NEVER the pre-edit body, NEVER a
 *   diff).
 * - `isTombstoned` is the result of `TombstoneStore.isDeleted(messageId)`.
 *   The caller is required to consult the tombstone store keyed by
 *   `(messageId, originalAuthorId)` BEFORE rendering, so an out-of-order
 *   tombstone (arrived before the cleartext) is honoured.
 * - `isEdited` is true iff an edit-for-everyone has been applied.
 * - `thumbnail` is the result of `resolveNotificationThumbnail`. It is a
 *   closed union — the caller cannot smuggle a remote URL through this
 *   field.
 */
export interface SWPolishRenderInputs {
  /** Notification title, already resolved from the local roster. */
  readonly title: string;
  /** Latest decrypted body. Empty string permitted. */
  readonly body: string;
  /** True when an edit-for-everyone has landed for this message. */
  readonly isEdited: boolean;
  /** True when a tombstone has been recorded for this messageId. */
  readonly isTombstoned: boolean;
  /** Thread id for reply routing — `channelId` or `1on1:<peerUserId>`. */
  readonly threadId: string;
  /** Thumbnail resolution — closed union, no remote-fetch variant. */
  readonly thumbnail: NotificationThumbnailResolution;
}

// ---------------------------------------------------------------------------
// Output shape — closed discriminated union
// ---------------------------------------------------------------------------

/**
 * What the SW push handler should display.
 *
 * The `iconBytes` field is `Uint8Array` ONLY when the thumbnail came from
 * the local cache. There is no `iconUrl` field, no `iconRemoteUrl` field —
 * the closed shape prevents a developer from accidentally passing a remote
 * URL into the icon path.
 */
export interface SWPolishedRender {
  readonly title: string;
  /** Final body — already collapsed/edited by the composer. */
  readonly body: string;
  readonly threadId: string;
  /** Cached thumbnail bytes. Absent when the cache missed. */
  readonly iconBytes?: Uint8Array;
}

/**
 * Closed return type.
 *
 * `suppress` is used by the SW polish step when there is no point rendering
 * AT ALL — e.g. an empty body that is also tombstoned and unedited (defensive;
 * the caller usually filters this earlier). Reasons are split so logs and
 * metrics can pinpoint WHY a notification was suppressed.
 */
export type SWPolishDecision =
  | { readonly kind: "render"; readonly display: SWPolishedRender }
  | { readonly kind: "suppress"; readonly reason: SWPolishSuppressReason };

export type SWPolishSuppressReason =
  | "emptyTitle"
  | "emptyBodyAndNoState";

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Decide the polished render shape for a freshly-decrypted SW push.
 *
 * Steps:
 *   1. Validate the title is non-empty (defensive — `SWRenderPolicy` should
 *      have already set a fallback like `"Message"`).
 *   2. Compose the final body via `composeNotificationBody`:
 *      - tombstone collapses to `[deleted]`,
 *      - edit appends `(edited)` exactly once,
 *      - otherwise body is passed through unchanged.
 *   3. If the resulting body is empty AND we have neither an edit nor a
 *      tombstone to communicate, suppress — there is no information to
 *      show.
 *   4. Build the display, attaching `iconBytes` ONLY when the thumbnail
 *      resolution is `{ kind: 'fromCache' }`.
 *
 * Pure function. No side effects.
 */
export function decideNotificationRender(
  inputs: SWPolishRenderInputs,
): SWPolishDecision {
  if (inputs.title.length === 0) {
    return { kind: "suppress", reason: "emptyTitle" };
  }

  const body = composeNotificationBody({
    body: inputs.body,
    isEdited: inputs.isEdited,
    isTombstoned: inputs.isTombstoned,
  });

  if (body.length === 0 && !inputs.isEdited && !inputs.isTombstoned) {
    return { kind: "suppress", reason: "emptyBodyAndNoState" };
  }

  // Closed-union switch: either we have cached bytes, or we have none. No
  // third path. A compiler-level exhaustiveness check would flag a future
  // `fetchedRemote` variant.
  const display: SWPolishedRender =
    inputs.thumbnail.kind === "fromCache"
      ? {
          title: inputs.title,
          body,
          threadId: inputs.threadId,
          iconBytes: inputs.thumbnail.bytes,
        }
      : {
          title: inputs.title,
          body,
          threadId: inputs.threadId,
        };

  return { kind: "render", display };
}
